import { useEffect, useState } from "react";
import { getContactData, postContactData } from "../../../api/apiServices";
import Button from "../../../components/base/Button";

export default function ContactEditor() {
  const [contactData, setContactData] = useState({
    sectionTitle: "",
    sectionSubtitle: "",
    formTitle: "",
    emailAddress: "",
    phoneNumber: "",
    availability: "",
    whyChooseMeTitle: "",
    benefits: [],
  });

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("⏳ Loading contact data...");

  // ✅ Fetch from backend
  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await getContactData();
        if (data) {
          setContactData(data);
          setMessage("✅ Loaded contact data from backend.");
        } else {
          setMessage("ℹ️ No existing contact data. Please fill out and save.");
        }
      } catch (error) {
        console.error("Failed to fetch contact data:", error);
        setMessage("❌ Failed to load from backend.");
      }
    };
    fetchData();
  }, []);

  // 🧠 Input handlers
  const handleInputChange = (field, value) => {
    setContactData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleBenefitChange = (index, field, value) => {
    setContactData((prev) => ({
      ...prev,
      benefits: prev.benefits.map((benefit, i) =>
        i === index ? { ...benefit, [field]: value } : benefit
      ),
    }));
  };

  const addBenefit = () => {
    setContactData((prev) => ({
      ...prev,
      benefits: [
        ...prev.benefits,
        {
          icon: "ri-star-line",
          title: "New Benefit",
          description: "Benefit description",
          iconColor: "text-blue-400",
          borderColor: "border-blue-400/30",
          gradient: ["from-blue-500/20 to-cyan-500/20"],
        },
      ],
    }));
  };

  const removeBenefit = (index) => {
    setContactData((prev) => ({
      ...prev,
      benefits: prev.benefits.filter((_, i) => i !== index),
    }));
  };

  // ✅ Save handler
  const handleSave = async () => {
    if (!contactData.sectionTitle || !contactData.formTitle || !contactData.emailAddress) {
      setMessage("⚠️ Please fill in Section Title, Form Title, and Email Address before saving.");
      return;
    }

    try {
      setLoading(true);
      setMessage("⏳ Saving contact section...");
      await postContactData(contactData);
      setMessage("✅ Contact section saved successfully!");
    } catch (error) {
      console.error(error);
      setMessage("❌ Failed to save contact section.");
    } finally {
      setLoading(false);
    }
  };

  // 🕐 Still loading
  if (!contactData) {
    return (
      <div className="p-6 text-center text-slate-600">
        <i className="ri-loader-4-line animate-spin text-2xl text-emerald-500 mb-2"></i>
        <p>{message}</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-slate-800 mb-6 flex items-center">
          <i className="ri-contacts-line mr-3 text-emerald-600"></i>
          Contact Section Settings
        </h2>
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 shadow-lg p-4 flex items-center justify-between z-50">
  <p className="text-slate-600 font-medium">
    {message || "All changes are saved locally until you click Save."}
  </p>
        <Button
          onClick={handleSave}
          disabled={loading}
          className="bg-emerald-600 hover:bg-emerald-700 text-white"
        >
          {loading ? (
            <>
              <i className="ri-loader-4-line animate-spin mr-2"></i> Saving...
            </>
          ) : (
            <>
              <i className="ri-save-3-line mr-2"></i> Save Section
            </>
          )}
        </Button>
        </div>
      </div>

      {message && (
        <p
          className={`text-sm font-medium ${
            message.startsWith("✅")
              ? "text-emerald-600"
              : message.startsWith("❌")
              ? "text-red-600"
              : "text-slate-600"
          }`}
        >
          {message}
        </p>
      )}

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Left Column */}
        <div className="space-y-6">
          {/* Section Header */}
          <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
            <h3 className="text-xl font-bold text-slate-800 mb-4">
              Section Header
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-slate-700 font-semibold mb-3">
                  Section Title *
                </label>
                <input
                  type="text"
                  value={contactData.sectionTitle}
                  onChange={(e) =>
                    handleInputChange("sectionTitle", e.target.value)
                  }
                  placeholder="Enter section title..."
                  className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-300"
                />
              </div>
              <div>
                <label className="block text-slate-700 font-semibold mb-3">
                  Section Subtitle
                </label>
                <textarea
                  value={contactData.sectionSubtitle}
                  onChange={(e) =>
                    handleInputChange("sectionSubtitle", e.target.value)
                  }
                  rows={3}
                  placeholder="Enter section subtitle..."
                  className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-300 resize-none"
                />
              </div>
            </div>
          </div>

          {/* Form Settings */}
          <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
            <h3 className="text-xl font-bold text-slate-800 mb-4">
              Form Settings
            </h3>
            <label className="block text-slate-700 font-semibold mb-3">
              Form Title *
            </label>
            <input
              type="text"
              value={contactData.formTitle}
              onChange={(e) => handleInputChange("formTitle", e.target.value)}
              placeholder="Enter form title..."
              className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-300"
            />
          </div>

          {/* Contact Info */}
          <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
            <h3 className="text-xl font-bold text-slate-800 mb-4">
              Contact Information
            </h3>
            <div className="space-y-4">
              {["emailAddress", "phoneNumber", "availability"].map((field) => (
                <div key={field}>
                  <label className="block text-slate-700 font-semibold mb-3 capitalize">
                    {field === "emailAddress" ? "Email Address *" : field}
                  </label>
                  <input
                    type="text"
                    value={contactData[field]}
                    onChange={(e) =>
                      handleInputChange(field, e.target.value)
                    }
                    placeholder={`Enter ${field.replace(/([A-Z])/g, " $1")}`}
                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-300"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-xl font-bold text-slate-800">
                Why Choose Me Section
              </h3>
              <label className="block text-slate-700 font-semibold mb-3 mt-2">
                Section Title
              </label>
              <input
                type="text"
                value={contactData.whyChooseMeTitle}
                onChange={(e) =>
                  handleInputChange("whyChooseMeTitle", e.target.value)
                }
                placeholder="Why Choose Me?"
                className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-300"
              />
            </div>
            <Button
              onClick={addBenefit}
              size="sm"
              variant="outline"
              className="border-emerald-200 text-emerald-600 hover:bg-emerald-50"
            >
              <i className="ri-add-line mr-2"></i>
              Add Benefit
            </Button>
          </div>

          <div className="space-y-4">
            {contactData.benefits?.map((benefit, index) => (
              <div
                key={benefit._id || index}
                className="bg-white rounded-xl p-4 border border-slate-200"
              >
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-slate-800">
                    Benefit {index + 1}
                  </h4>
                  {contactData.benefits.length > 1 && (
                    <button
                      onClick={() => removeBenefit(index)}
                      className="text-red-500 hover:text-red-700 transition-colors duration-300"
                    >
                      <i className="ri-delete-bin-line text-lg"></i>
                    </button>
                  )}
                </div>

                <div className="space-y-3">
                  <div className="grid md:grid-cols-2 gap-3">
                    <input
                      type="text"
                      value={benefit.icon || ""}
                      onChange={(e) =>
                        handleBenefitChange(index, "icon", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                      placeholder="ri-time-line"
                    />
                    <input
                      type="text"
                      value={benefit.iconColor || ""}
                      onChange={(e) =>
                        handleBenefitChange(index, "iconColor", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                      placeholder="text-emerald-400"
                    />
                  </div>
                  <input
                    type="text"
                    value={benefit.title || ""}
                    onChange={(e) =>
                      handleBenefitChange(index, "title", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                    placeholder="Benefit title"
                  />
                  <textarea
                    rows={2}
                    value={benefit.description || ""}
                    onChange={(e) =>
                      handleBenefitChange(index, "description", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm resize-none"
                    placeholder="Benefit description"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
